import React, { useEffect, useState } from 'react';

/**
 * In-app browser escape hatch.
 *
 * Instagram, Facebook, and TikTok all funnel link taps through their
 * stripped-down in-app browsers, which routinely break:
 *   - Stripe / OAuth flows
 *   - Service workers and 3rd-party cookies
 *   - Some client-side route transitions
 *
 * When we detect we're being rendered inside one of those WebViews, we
 * show a banner with a one-tap "Open in browser" CTA so the user can get
 * to the real Chrome/Safari app where everything works correctly.
 *
 * Detection is based on user agent — same heuristic Stripe and other
 * large platforms use. Dismissed state is remembered in sessionStorage
 * so the banner doesn't nag on every page in the same session.
 */
const STORAGE_KEY = 'inapp-browser-banner-dismissed';

function detectInAppBrowser(ua) {
  if (!ua) return null;
  const lower = ua.toLowerCase();
  // Order matters — check most specific markers first.
  if (lower.includes('instagram')) return 'Instagram';
  if (/fb_iab|fbav|fban|fb_ref|fbios/.test(lower)) return 'Facebook';
  if (lower.includes('tiktok') || lower.includes('musical_ly')) return 'TikTok';
  if (/twitter|tweetdeck/.test(lower) && lower.includes('mobile')) return 'Twitter';
  if (lower.includes('linkedinapp')) return 'LinkedIn';
  if (lower.includes('snapchat')) return 'Snapchat';
  return null;
}

export default function InAppBrowserBanner() {
  const [appName, setAppName] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const detected = detectInAppBrowser(navigator.userAgent || '');
    setAppName(detected);
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') setDismissed(true);
    } catch {
      /* private mode — ignore */
    }
  }, []);

  if (!appName || dismissed) return null;

  const openInBrowser = () => {
    const url = window.location.href;
    const ua = navigator.userAgent.toLowerCase();
    const isAndroid = /android/.test(ua);

    if (isAndroid) {
      // Android intent URL forces the system to open the link in the
      // default browser instead of the in-app WebView.
      const cleanUrl = url.replace(/^https?:\/\//, '');
      window.location.href = `intent://${cleanUrl}#Intent;scheme=https;package=com.android.chrome;end`;
    } else {
      // iOS doesn't allow forcing Safari from a WebView, so the best
      // we can do is provide a copy-to-clipboard fallback. Most modern
      // iOS browsers will respect a target=_blank as a hint though.
      try {
        navigator.clipboard?.writeText(url);
      } catch {
        /* ignore */
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const dismiss = () => {
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="fixed top-0 inset-x-0 z-[9999] bg-[#00F0FF] text-black px-4 py-3 shadow-lg"
      data-testid="inapp-browser-banner"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
    >
      <div className="max-w-3xl mx-auto flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold leading-tight">
            You're in the {appName} browser
          </p>
          <p className="text-xs leading-tight opacity-80 mt-0.5">
            Tap below for the best experience (sign in, checkout, and more).
          </p>
        </div>
        <button
          onClick={openInBrowser}
          className="shrink-0 bg-black text-[#00F0FF] font-bold text-sm px-4 py-2 rounded-lg hover:bg-neutral-800 transition-colors whitespace-nowrap"
          data-testid="open-in-browser-btn"
        >
          Open in browser
        </button>
        <button
          onClick={dismiss}
          className="shrink-0 text-black/60 hover:text-black text-xl leading-none px-2"
          aria-label="Dismiss"
          data-testid="dismiss-inapp-banner"
        >
          ×
        </button>
      </div>
    </div>
  );
}
